import * as THREE from 'three';
import { BLACK, DSTEEL, DWOOD, STRING, WOOD, cloth, emis, metal, stone } from '../materials.ts';
import { G, M, V, glowSprite, limb, ring } from '../prims.ts';
import { CRYSTAL, EMBER, FORGE } from './land.ts';
import { IRON_BAND, IRON_PLATE, IRON_RIVET, type Wear } from './structures.ts';
import type { Model } from './types.ts';

/*
 * Tower branches (docs/OBRAS.md §3, content BRANCHES): a level 4 tower that took a branch wears it as a crown on
 * the model it already has, so the branch reads from the table camera: twin bows or a barbed harpoon and its reel
 * on the ballista, a smoking pitch pot or a long arm with a great weight on the catapult, one tall crystal or a
 * ring of crystals pointing at the sky on the arcane tower, a boiler with its pipes or an iron-banded sealed
 * casing on the clockwork turret, a cracked stone or a brazier pot in the trebuchet's sling.
 *
 * Geometry only, over the shared paints: the lit parts reuse self-lit paints other models already have
 * (materials.ts caches by parameters), nothing see-through or two-sided, no lights; glows are sprites the bake
 * moves to the glow layer. What turns with the tower goes under its part ('turret', 'arm', 'crystal'), so it
 * follows the aim.
 */

export type BranchKey = 'repeater' | 'harpoon' | 'pitch' | 'counterweight' | 'focus' | 'sky' | 'steam' | 'sealed' | 'split' | 'embers';

/** Which tower each branch belongs to (content BRANCHES[b].tower). */
export const BRANCH_TOWER: Record<BranchKey, string> = {
  repeater: 'ballista',
  harpoon: 'ballista',
  pitch: 'catapult',
  counterweight: 'catapult',
  focus: 'arcane',
  sky: 'arcane',
  steam: 'clockwork',
  sealed: 'clockwork',
  split: 'trebuchet',
  embers: 'trebuchet',
};

/** Dragon-red rune light (the level 4 pedestal's runes, upgrades.ts). */
const RED = emis('#ffc0a8', '#ff4f3a', 1.3);
/** The Vigil's pale rune light (the level 5 pedestal). */
const PALE = emis('#f2f6ff', '#bcd0ff', 1.4);
/** The arcane halo's rune light (structures.ts arcane, level 2). */
const HALO = emis('#cfefff', '#5ab8ff', 1);
const COPPER = metal('#a4512f', { roughness: 0.4 });
const ROCK = stone('#6d6559');
const SMOKE = stone('#3a3634');
const CRIMSON = cloth('#7a1d22');

const box = (parent: THREE.Object3D, mat: THREE.Material, w: number, h: number, d: number, x: number, y: number, z: number): THREE.Mesh => {
  const m = M(new THREE.BoxGeometry(w, h, d), mat, parent);
  m.position.set(x, y, z);
  return m;
};

/** A bow arc across +Z (the ballista's), `w` half-wide, at height y, strung back to the stock. */
const bow = (t: THREE.Object3D, y: number, w: number, z0: number, mat: THREE.Material): void => {
  const pts: THREE.Vector3[] = [];
  for (let i = 0; i <= 10; i++) {
    const a = -1 + (i / 10) * 2;
    pts.push(V(Math.sin(a) * w, y, z0 - Math.cos(a) * 0.12));
  }
  M(new THREE.TubeGeometry(new THREE.CatmullRomCurve3(pts), 12, 0.02, 5), mat, t);
  limb(t, pts[0]!, V(0, y, -0.05), 0.004, 0.004, STRING, 3);
  limb(t, pts[10]!, V(0, y, -0.05), 0.004, 0.004, STRING, 3);
};

/** A pot of fire: a black iron pot, its coals and the glow (`r` its radius), at (x, y, z) in `p`. */
const firePot = (p: THREE.Object3D, x: number, y: number, z: number, r: number, lit: number): void => {
  M(new THREE.CylinderGeometry(r, r * 0.75, r * 1.2, 8), BLACK, p).position.set(x, y, z);
  M(new THREE.CylinderGeometry(r * 0.88, r * 0.88, 0.012, 8), EMBER, p).position.set(x, y + r * 0.6, z);
  for (let i = 0; i < 3; i++) {
    const a = (i / 3) * Math.PI * 2;
    M(new THREE.DodecahedronGeometry(r * 0.32, 0), FORGE, p).position.set(x + Math.sin(a) * r * 0.4, y + r * 0.68, z + Math.cos(a) * r * 0.4);
  }
  glowSprite(p, '#ff7a2a', r * 5.5, V(x, y + r * 0.9, z), 0.7 * lit);
};

/** A column of soot puffs rising from (x, y, z), leaning off to +X. */
const smoke = (p: THREE.Object3D, x: number, y: number, z: number, s: number): void => {
  for (const [dx, dy, k] of [[0.01, 0.06, 0.7], [0.05, 0.16, 0.9], [0.1, 0.28, 1]] as const) {
    M(new THREE.DodecahedronGeometry(s * k, 0), SMOKE, p).position.set(x + dx, y + dy, z + dx * 0.3);
  }
};

/**
 * Adds `branch`'s crown to a tower model (its own, before the pedestal raises it). A branch that isn't this
 * tower's leaves it as it is.
 */
export const branchCrown = (m: Model, type: string, branch: string, wear: Wear = 0): Model => {
  if (BRANCH_TOWER[branch as BranchKey] !== type) return m;
  const g = (m.root.getObjectByName('model') as THREE.Group | undefined) ?? m.root;
  const lit = [1, 0.75, 0.45][wear] as number;
  switch (branch as BranchKey) {
    case 'repeater': {
      // A second bow over the first on its own stock, joined by cheeks, and a hopper of bolts feeding both.
      const t = m.root.getObjectByName('turret');
      if (!t) break;
      const y = 0.17;
      box(t, DWOOD, 0.07, 0.07, 0.56, 0, y, 0.02);
      bow(t, y, 0.36, 0.18, DSTEEL);
      for (const k of [-1, 1]) box(t, DWOOD, 0.03, y + 0.04, 0.03, k * 0.06, y / 2, 0.1);
      const tip = M(new THREE.ConeGeometry(0.03, 0.15, 6), DSTEEL, t);
      tip.rotation.x = Math.PI / 2;
      tip.position.set(0, y, 0.37);
      const hop = G(t, 0, y + 0.1, -0.08);
      box(hop, DWOOD, 0.2, 0.1, 0.16, 0, 0, 0);
      for (const z of [-0.06, 0.06]) box(hop, IRON_BAND, 0.21, 0.02, 0.012, 0, 0.0, z + Math.sign(z) * 0.023);
      for (let i = 0; i < 4; i++) {
        const b = M(new THREE.ConeGeometry(0.016, 0.06, 4), DSTEEL, hop);
        b.position.set(-0.06 + i * 0.04, 0.075, 0.03);
      }
      break;
    }
    case 'harpoon': {
      // A long barbed iron harpoon laid in the stock, out past the bow, and the reel of rope at its side.
      const t = m.root.getObjectByName('turret');
      if (!t) break;
      const sh = M(new THREE.CylinderGeometry(0.024, 0.024, 0.95, 6), DSTEEL, t);
      sh.rotation.x = Math.PI / 2;
      sh.position.set(0, 0.07, 0.28);
      const head = M(new THREE.ConeGeometry(0.085, 0.26, 6), DSTEEL, t);
      head.rotation.x = Math.PI / 2;
      head.position.set(0, 0.07, 0.88);
      for (const k of [-1, 1]) {
        const barb = M(new THREE.ConeGeometry(0.03, 0.18, 4), IRON_RIVET, t);
        barb.position.set(k * 0.07, 0.07, 0.73);
        barb.rotation.set(-Math.PI / 2, 0, k * -0.6);
      }
      box(t, IRON_BAND, 0.05, 0.05, 0.04, 0, 0.07, 0.68);
      // The reel on the right of the stock, rope wound on it and run out to the shaft's eye.
      const reel = G(t, 0.2, 0.0, -0.1);
      const drum = M(new THREE.CylinderGeometry(0.09, 0.09, 0.12, 10), WOOD, reel);
      drum.rotation.z = Math.PI / 2;
      for (const x of [-0.055, 0.055]) {
        const fl = M(new THREE.CylinderGeometry(0.125, 0.125, 0.016, 10), DSTEEL, reel);
        fl.rotation.z = Math.PI / 2;
        fl.position.x = x;
      }
      for (const x of [-0.03, 0, 0.03]) {
        const coil = M(new THREE.TorusGeometry(0.093, 0.014, 4, 12), STRING, reel);
        coil.rotation.y = Math.PI / 2;
        coil.position.x = x;
      }
      limb(t, V(0.2, 0.06, -0.06), V(0.02, 0.08, 0.6), 0.005, 0.005, STRING, 3);
      box(t, DWOOD, 0.04, 0.14, 0.04, 0.2, -0.08, -0.1);
      break;
    }
    case 'pitch': {
      // A pot of burning pitch in the cup, and a cauldron smoking on the bed with a ladle.
      const arm = m.root.getObjectByName('arm');
      if (arm) firePot(arm, 0.55, 0.11, 0, 0.075, lit);
      const cg = G(g, -0.24, 0.24, -0.1);
      for (let i = 0; i < 5; i++) {
        const a = (i / 5) * Math.PI * 2;
        M(new THREE.DodecahedronGeometry(0.035, 0), ROCK, cg).position.set(Math.sin(a) * 0.1, 0.02, Math.cos(a) * 0.1);
      }
      firePot(cg, 0, 0.08, 0, 0.1, lit);
      smoke(cg, 0, 0.22, 0, 0.07);
      limb(cg, V(0.04, 0.12, 0.04), V(0.16, 0.24, 0.12), 0.008, 0.008, DSTEEL, 3);
      break;
    }
    case 'counterweight': {
      // The arm doubled and lengthened, a sling at its new end, a great iron-bound weight under its short end.
      const arm = m.root.getObjectByName('arm');
      if (!arm) break;
      limb(arm, V(-0.44, -0.035, 0), V(0.92, -0.03, 0), 0.03, 0.02, DWOOD, 6);
      for (const x of [-0.2, 0.3, 0.75]) box(arm, IRON_BAND, 0.03, 0.09, 0.07, x, -0.02, 0);
      for (const k of [-1, 1]) limb(arm, V(0.92, -0.03, 0), V(0.9, -0.16, k * 0.04), 0.004, 0.004, STRING, 3);
      M(new THREE.DodecahedronGeometry(0.075, 0), ROCK, arm).position.set(0.9, -0.2, 0);
      const wg = G(arm, -0.42, -0.08, 0);
      for (const k of [-1, 1]) limb(wg, V(0, 0, k * 0.05), V(0, -0.1, k * 0.13), 0.012, 0.012, DSTEEL, 4);
      box(wg, stone('#4f4a44'), 0.32, 0.26, 0.3, 0, -0.24, 0);
      for (const y of [-0.15, -0.33]) box(wg, IRON_BAND, 0.34, 0.03, 0.32, 0, y, 0);
      for (const k of [-1, 1]) M(new THREE.OctahedronGeometry(0.02, 0), IRON_RIVET, wg).position.set(k * 0.1, -0.24, 0.155);
      // Taller posts with a brace, and a block and tackle for winding it down.
      for (const z of [-0.26, 0.26]) {
        limb(g, V(-0.42, 0.24, z), V(-0.42, 0.95, z), 0.03, 0.025, WOOD, 6);
        limb(g, V(-0.42, 0.95, z), V(0, 0.7, z), 0.022, 0.022, DWOOD, 5);
      }
      box(g, DWOOD, 0.06, 0.06, 0.6, -0.42, 0.95, 0);
      limb(g, V(-0.42, 0.93, 0), V(-0.42, 0.62, 0), 0.004, 0.004, STRING, 3);
      break;
    }
    case 'focus': {
      // One tall needle of crystal rising out of the great one, a steel collar round its foot.
      const cr = m.root.getObjectByName('crystal');
      const p = cr ?? g;
      const y0 = cr ? 0 : 1.65;
      const needle = M(new THREE.OctahedronGeometry(0.1, 0), CRYSTAL, p);
      needle.scale.set(1, 5.2, 1);
      needle.position.y = y0 + 0.82;
      ring(p, 0.11, 0.018, DSTEEL, y0 + 0.36, 4, 10);
      ring(p, 0.075, 0.012, HALO, y0 + 0.9, 3, 10);
      glowSprite(p, '#3aa6ff', 0.55, V(0, y0 + 1.25, 0), 0.6 * lit);
      // Two stone fins on the cradle point it up.
      for (const k of [-1, 1]) limb(g, V(k * 0.22, 1.0, 0), V(k * 0.12, 1.42, 0), 0.04, 0.02, stone('#4d4a55'), 5);
      break;
    }
    case 'sky': {
      // A collar of stone round the cradle and six crystals on it, leaning out and pointing at the sky.
      ring(g, 0.4, 0.045, stone('#4d4a55'), 1.2, 4, 18);
      for (let i = 0; i < 6; i++) {
        const a = (i / 6) * Math.PI * 2 + 0.25;
        const cg = G(g, Math.sin(a) * 0.4, 1.26, Math.cos(a) * 0.4);
        cg.rotation.set(Math.cos(a) * 0.32, 0, -Math.sin(a) * 0.32);
        M(new THREE.ConeGeometry(0.045, 0.06, 5), DSTEEL, cg).position.y = -0.01;
        const c = M(new THREE.OctahedronGeometry(0.06, 0), CRYSTAL, cg);
        c.scale.y = 3;
        c.position.y = 0.17;
        if (i % 2 === 0) glowSprite(cg, '#7fd0ff', 0.3, V(0, 0.2, 0), 0.5 * lit);
      }
      ring(g, 0.4, 0.008, HALO, 1.25, 3, 24);
      break;
    }
    case 'steam': {
      // A copper boiler standing on the head's back, its stack smoking, two pipes to a flared steam muzzle.
      const t = m.root.getObjectByName('turret');
      if (!t) break;
      const by = 0.36, bz = -0.16;
      M(new THREE.CylinderGeometry(0.09, 0.09, 0.24, 10), COPPER, t).position.set(0, by, bz);
      const dome = M(new THREE.SphereGeometry(0.09, 10, 5, 0, Math.PI * 2, 0, Math.PI / 2), IRON_BAND, t);
      dome.position.set(0, by + 0.12, bz);
      for (const y of [by - 0.07, by + 0.05]) ring(t, 0.093, 0.01, IRON_BAND, y, 3, 14).position.set(0, y, bz);
      M(new THREE.CylinderGeometry(0.022, 0.028, 0.14, 6), IRON_BAND, t).position.set(0.04, by + 0.22, bz);
      smoke(t, 0.04, by + 0.3, bz, 0.05);
      for (const k of [-1, 1]) limb(t, V(k * 0.08, by - 0.04, bz + 0.04), V(k * 0.05, 0.13, 0.3), 0.014, 0.014, COPPER, 5);
      const muzzle = M(new THREE.CylinderGeometry(0.075, 0.035, 0.12, 10, 1, false), DSTEEL, t);
      muzzle.rotation.x = Math.PI / 2;
      muzzle.position.set(0, 0.13, 0.36);
      const bore = M(new THREE.CylinderGeometry(0.05, 0.05, 0.01, 10), BLACK, t);
      bore.rotation.x = Math.PI / 2;
      bore.position.set(0, 0.13, 0.42);
      const gauge = M(new THREE.CylinderGeometry(0.028, 0.028, 0.012, 10), IRON_RIVET, t);
      gauge.rotation.x = Math.PI / 2;
      gauge.position.set(0, by, bz + 0.093);
      box(t, EMBER, 0.05, 0.03, 0.01, 0, by - 0.09, bz + 0.092);
      break;
    }
    case 'sealed': {
      // An iron casing domed over the head, banded and riveted, a crimson seal stamped with a pale rune.
      const t = m.root.getObjectByName('turret');
      if (!t) break;
      const cy = 0.2, cz = -0.03;
      const dome = M(new THREE.SphereGeometry(0.2, 12, 6, 0, Math.PI * 2, 0, Math.PI / 2), IRON_PLATE, t);
      dome.position.set(0, cy, cz);
      dome.scale.set(1, 0.85, 1.05);
      M(new THREE.CylinderGeometry(0.205, 0.205, 0.05, 12, 1, true), IRON_BAND, t).position.set(0, cy + 0.01, cz);
      for (const r of [0, Math.PI / 2]) {
        const strap = M(new THREE.TorusGeometry(0.2, 0.014, 4, 12, Math.PI), IRON_BAND, t);
        strap.position.set(0, cy, cz);
        strap.rotation.y = r;
        strap.scale.y = 0.86;
      }
      for (let i = 0; i < 8; i++) {
        const a = (i / 8) * Math.PI * 2;
        M(new THREE.OctahedronGeometry(0.013, 0), IRON_RIVET, t).position.set(Math.sin(a) * 0.21, cy + 0.01, cz + Math.cos(a) * 0.21);
      }
      const seal = G(t, 0, cy + 0.11, cz + 0.16);
      seal.rotation.x = -0.75;
      M(new THREE.CylinderGeometry(0.06, 0.065, 0.025, 10), CRIMSON, seal).rotation.x = Math.PI / 2;
      // A stroke under a chevron, the titan runes' shape (not a cross).
      box(seal, PALE, 0.014, 0.05, 0.008, 0, -0.008, 0.015);
      for (const k of [-1, 1]) box(seal, PALE, 0.035, 0.012, 0.008, k * 0.012, 0.026, 0.015).rotation.z = k * -0.6;
      glowSprite(seal, '#dfe8ff', 0.2, V(0, 0, 0.03), 0.55 * lit);
      // Two hanging ribbons from the seal.
      for (const k of [-1, 1]) box(seal, CRIMSON, 0.025, 0.08, 0.008, k * 0.03, -0.07, 0.0);
      break;
    }
    case 'split': {
      // A great stone cracked in three in the sling, its seams burning red; a heap of split stones and a wedge by the sled.
      const arm = m.root.getObjectByName('arm');
      if (arm) {
        const sg = G(arm, 1.09, -0.15, 0);
        for (let i = 0; i < 3; i++) {
          const a = (i / 3) * Math.PI * 2;
          const ch = M(new THREE.DodecahedronGeometry(0.065, 0), ROCK, sg);
          ch.position.set(Math.sin(a) * 0.045, Math.cos(a) * 0.045, 0);
          ch.scale.set(0.9, 0.9, 1.1);
        }
        for (let i = 0; i < 3; i++) {
          const a = (i / 3) * Math.PI * 2 + Math.PI / 3;
          const seam = box(sg, RED, 0.012, 0.08, 0.13, Math.sin(a) * 0.02, Math.cos(a) * 0.02, 0);
          seam.rotation.z = -a;
        }
        glowSprite(sg, '#ff4f3a', 0.45, V(0, 0, 0), 0.65 * lit);
      }
      const pile = G(g, -0.5, 0.0, -0.4);
      for (const [x, z, r] of [[0, 0, 0.3], [0.12, 0.05, 1.4], [0.05, 0.13, 2.2], [-0.1, 0.08, 3]] as const) {
        const half = M(new THREE.SphereGeometry(0.07, 7, 4, 0, Math.PI * 2, 0, Math.PI / 2), ROCK, pile);
        half.position.set(x, 0, z);
        half.rotation.y = r;
      }
      const wedge = M(new THREE.ConeGeometry(0.03, 0.12, 4), DSTEEL, pile);
      wedge.position.set(0.05, 0.1, 0.0);
      wedge.rotation.z = Math.PI;
      break;
    }
    case 'embers': {
      // A pot of embers in the sling, and a brazier on a tripod by the sled to light the next.
      const arm = m.root.getObjectByName('arm');
      if (arm) {
        for (const k of [-1, 1]) limb(arm, V(1.1, -0.02, 0), V(1.08, -0.15, k * 0.07), 0.005, 0.005, DSTEEL, 3);
        firePot(arm, 1.08, -0.21, 0, 0.085, lit);
      }
      const br = G(g, -0.6, 0, -0.34);
      for (let i = 0; i < 3; i++) {
        const a = (i / 3) * Math.PI * 2;
        limb(br, V(Math.sin(a) * 0.12, 0, Math.cos(a) * 0.12), V(Math.sin(a) * 0.05, 0.3, Math.cos(a) * 0.05), 0.01, 0.01, DSTEEL, 4);
      }
      const bowl = M(new THREE.CylinderGeometry(0.11, 0.06, 0.08, 8), DSTEEL, br);
      bowl.position.y = 0.33;
      for (let i = 0; i < 4; i++) {
        const a = (i / 4) * Math.PI * 2;
        M(new THREE.DodecahedronGeometry(0.035, 0), FORGE, br).position.set(Math.sin(a) * 0.05, 0.38, Math.cos(a) * 0.05);
      }
      M(new THREE.CylinderGeometry(0.095, 0.095, 0.01, 8), EMBER, br).position.y = 0.37;
      glowSprite(br, '#ff7a2a', 0.6, V(0, 0.45, 0), 0.75 * lit);
      // An iron-red pennant on the frame: the fire's sign.
      limb(g, V(-0.38, 0.12, 0.26), V(-0.38, 1.1, 0.26), 0.012, 0.012, DWOOD, 4);
      box(g, CRIMSON, 0.2, 0.14, 0.01, -0.27, 1.02, 0.26);
      break;
    }
  }
  return m;
};
